import React, { useState, useMemo, useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { useTranslation } from 'react-i18next';
import {
  FileText,
  Calendar,
  BarChart3,
  Link2,
  PenSquare,
  Layers,
  Heart,
  MousePointerClick,
  Send,
  Sparkles,
  TrendingUp,
  TrendingDown,
  Clock,
  ArrowUpRight,
  Rss,
  Twitter,
  Facebook,
  Instagram,
  Linkedin,
  Plus,
  AlertTriangle,
} from "lucide-react";
import { analyticsAPI, postsAPI, socialAPI, pagesAPI } from "../../services/api";
import { useAuthStore } from "../../store/authStore";
import OnboardingChecklist, {
  getDefaultOnboardingSteps,
} from "../onboarding/OnboardingChecklist";

// ── Animated Counter ─────────────────────────────────────
function AnimatedCounter({
  value,
  duration = 800,
}: {
  value: number;
  duration?: number;
}) {
  const [count, setCount] = useState(0);

  useEffect(() => {
    let start = 0;
    const increment = value / (duration / 16);
    let timer: ReturnType<typeof setTimeout>;

    const animateCount = () => {
      start += increment;
      if (start >= value) {
        setCount(value);
        return;
      }
      setCount(Math.floor(start));
      timer = setTimeout(animateCount, 16);
    };

    animateCount();
    return () => clearTimeout(timer);
  }, [value, duration]);

  return <>{count.toLocaleString()}</>;
}

// ── Stat Card ───────────────────────────────────────────
function StatCard({
  label,
  value,
  change,
  Icon,
  iconBg,
  iconColor,
  index = 0,
}: {
  label: string;
  value: string | number;
  change?: number;
  Icon: React.ElementType;
  iconBg: string;
  iconColor: string;
  index?: number;
}) {
  const numValue = typeof value === "number" ? value : parseInt(value as string) || 0;
  const shouldAnimate = typeof value === "number" && value > 0;

  return (
    <div
      className="n-card group p-4 transition-all duration-200 hover:-translate-y-0.5 hover:shadow-n animate-fade-in-up"
      style={{ animationDelay: `${0.1 + index * 0.05}s` }}
    >
      <div className="flex items-center gap-4">
        <div className={`w-10 h-10 ${iconBg} rounded-n flex items-center justify-center flex-shrink-0`}>
          <Icon className={`w-5 h-5 ${iconColor}`} />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-xs font-medium text-n-text-lt mb-0.5">{label}</p>
          <div className="flex items-baseline gap-2">
            <h3 className="text-xl font-bold text-n-text tracking-tight">
              {shouldAnimate ? <AnimatedCounter value={numValue} /> : value}
            </h3>
            {change !== undefined && (
              <span className={`inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-n-xs text-[10px] font-semibold ${
                change >= 0 ? "bg-n-green-bg text-n-green" : "bg-n-red-bg text-n-red"
              }`}>
                {change >= 0 ? <TrendingUp size={10} /> : <TrendingDown size={10} />}
                {change >= 0 ? "+" : ""}{change}%
              </span>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Platform Icon Component ─────────────────────────────
function PlatformIcon({
  platform,
  size = 14,
}: {
  platform: string;
  size?: number;
}) {
  const iconProps = { size, strokeWidth: 2 };
  const platformLower = platform?.toLowerCase() || "";

  if (platformLower.includes("twitter") || platformLower.includes("x")) {
    return <Twitter {...iconProps} className="text-n-text-lt" />;
  }
  if (platformLower.includes("facebook")) {
    return <Facebook {...iconProps} className="text-n-blue" />;
  }
  if (platformLower.includes("instagram")) {
    return <Instagram {...iconProps} className="text-n-red" />;
  }
  if (platformLower.includes("linkedin")) {
    return <Linkedin {...iconProps} className="text-n-blue" />;
  }
  return <Rss {...iconProps} className="text-n-text-lt" />;
}

// ── Account tile helpers ────────────────────────────────
// Used by the Connected Accounts card to render an initials-tile
// fallback when a platform avatar URL is missing or returns an error
// (e.g. expired TikTok CDN tokens produce a broken <img>).

/** Return up to 2 uppercase initials from a display name or username. */
function getAccountInitials(name: string): string {
  const cleaned = (name || '').replace(/[._-]+/g, ' ').trim();
  if (!cleaned) {
    return '?';
  }
  const parts = cleaned.split(/\s+/).filter(Boolean);
  if (parts.length >= 2) {
    return (parts[0][0] + parts[1][0]).toUpperCase();
  }
  // Single word — take the first two letters so we don't get a
  // single-letter tile for handles like "crab.4974608".
  return cleaned.slice(0, 2).toUpperCase();
}

/**
 * Pick a deterministic pastel background for the initials tile so the
 * same account always renders the same colour across sessions.
 */
function getAccountTileStyle(name: string): { backgroundColor: string; color: string } {
  const palette = [
    { bg: '#fef3c7', fg: '#78350f' }, // amber
    { bg: '#dbeafe', fg: '#1e3a8a' }, // blue
    { bg: '#d1fae5', fg: '#065f46' }, // green
    { bg: '#fce7f3', fg: '#831843' }, // pink
    { bg: '#ede9fe', fg: '#4c1d95' }, // violet
    { bg: '#ffedd5', fg: '#7c2d12' }, // orange
    { bg: '#cffafe', fg: '#164e63' }, // cyan
  ];
  let hash = 0;
  const src = name || '';
  for (let i = 0; i < src.length; i++) {
    hash = (hash * 31 + src.charCodeAt(i)) | 0;
  }
  const pick = palette[Math.abs(hash) % palette.length];
  return { backgroundColor: pick.bg, color: pick.fg };
}

// ── Recent Post Row ─────────────────────────────────────
interface DashboardPost {
  id: string;
  text?: string;
  status: string;
  scheduled_at?: string;
  published_at?: string;
  platforms?: string[];
  media_thumbnail?: string;
  total_likes?: number;
  total_comments?: number;
  total_shares?: number;
  avatar_url?: string;
  username?: string;
  display_name?: string;
  platform?: string;
}

function RecentPostRow({ post }: { post: DashboardPost }) {
  const statusConfig: Record<string, { bg: string; text: string; dot: string }> = {
    published: { bg: "bg-n-green-bg", text: "text-n-green", dot: "bg-n-green" },
    scheduled: { bg: "bg-n-blue-bg", text: "text-n-blue", dot: "bg-n-blue" },
    draft: { bg: "bg-n-hover", text: "text-n-text-md", dot: "bg-n-text-lt" },
    failed: { bg: "bg-n-red-bg", text: "text-n-red", dot: "bg-n-red" },
  };
  const config = statusConfig[post.status] || statusConfig.draft;

  return (
    <div className="animate-fade-in-up">
      <Link
        to={`/compose/${post.id}`}
        className="flex items-center gap-4 py-3.5 px-5 hover:bg-n-hover transition-all border-b border-n-border last:border-0 group"
      >
        <div className="w-11 h-11 rounded-n overflow-hidden bg-n-hover border border-n-border flex-shrink-0 relative flex items-center justify-center">
          <FileText className="w-5 h-5 text-n-text-lt" />
          {post.media_thumbnail && (
            <img
              src={post.media_thumbnail}
              alt=""
              className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
              onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
            />
          )}
        </div>
        <div className="flex-1 min-w-0">
          <h4 className="text-sm font-medium text-n-text truncate group-hover:text-n-blue transition-colors tracking-tight">
            {post.text || "Untitled post"}
          </h4>
          <div className="flex items-center gap-3 mt-1.5">
            <div className="flex items-center gap-1.5 text-[11px] font-medium text-n-text-lt">
              <Clock size={12} />
              {post.scheduled_at
                ? new Date(post.scheduled_at).toLocaleDateString("en-US", {
                    month: "short",
                    day: "numeric",
                    hour: "numeric",
                    minute: "2-digit",
                  })
                : "No date"}
            </div>
            {(post.platforms?.length ?? 0) > 0 && (
              <div className="flex items-center gap-1.5 pl-3 border-l border-n-border">
                {(post.platforms ?? []).slice(0, 3).map((platform: string) => (
                  <PlatformIcon key={platform} platform={platform} size={12} />
                ))}
              </div>
            )}
          </div>
        </div>
        <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[10px] font-semibold uppercase tracking-wider ${config.bg} ${config.text} flex-shrink-0 transition-all`}>
          <span className={`w-1.5 h-1.5 rounded-full ${config.dot}`} />
          {post.status}
        </span>
      </Link>
    </div>
  );
}

// ── Upcoming Post Item ──────────────────────────────────
function UpcomingPostItem({ post }: { post: DashboardPost }) {
  const date = post.scheduled_at ? new Date(post.scheduled_at) : null;
  return (
    <div className="flex items-center gap-3 py-2.5 group">
      <div className="w-10 h-10 bg-n-blue rounded-n flex flex-col items-center justify-center flex-shrink-0">
        {date ? (
          <>
            <span className="text-[8px] font-semibold text-white/70 leading-none uppercase tracking-wide">
              {date.toLocaleDateString("en-US", { month: "short" })}
            </span>
            <span className="text-base font-bold text-white leading-none">
              {date.getDate()}
            </span>
          </>
        ) : (
          <Clock size={14} className="text-white/70" />
        )}
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-semibold text-n-text truncate">
          {post.text || "Untitled"}
        </p>
        <p className="text-xs text-n-text-lt mt-0.5">
          {date ? date.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" }) : "No time set"}
        </p>
      </div>
      <ArrowUpRight size={14} className="text-n-text-lt group-hover:text-n-text-md transition-colors flex-shrink-0" />
    </div>
  );
}

// ── Main Dashboard ──────────────────────────────────────
export default function DashboardPage() {
  const { user } = useAuthStore();
  const [showOnboarding, setShowOnboarding] = useState(true);

  const { data: overview } = useQuery({
    queryKey: ["analytics-overview"],
    queryFn: () => analyticsAPI.overview().then((r) => r.data),
  });

  const { data: recentPosts } = useQuery({
    queryKey: ["recent-posts"],
    queryFn: () => postsAPI.list({ page: 1 }).then((r) => r.data),
  });

  const { data: publishedPosts } = useQuery({
    queryKey: ["published-posts"],
    queryFn: () => postsAPI.list({ status: "published", page: 1 }).then((r) => r.data),
  });

  const { data: accounts } = useQuery({
    queryKey: ["social-accounts"],
    queryFn: () => socialAPI.list().then((r) => r.data),
  });

  const { data: pagesData } = useQuery({
    queryKey: ["pages-list"],
    queryFn: () => pagesAPI.list().then((r) => r.data),
  });

  const hasAccounts = (accounts?.accounts?.length || 0) > 0;
  const needsReauthCount = accounts?.needs_reauth_count || 0;
  const hasPosts = (recentPosts?.posts?.length || 0) > 0;
  const hasScheduled = recentPosts?.posts?.some((p: DashboardPost) => p.status === "scheduled") || false;
  const hasPage = (pagesData?.pages?.length || 0) > 0;
  const isNewUser = !hasAccounts && !hasPosts && !user?.onboarding_completed;

  const { t } = useTranslation();

  const onboardingSteps = useMemo(
    () => getDefaultOnboardingSteps(t, hasAccounts, hasPosts, hasScheduled, hasPage),
    [hasAccounts, hasPosts, hasScheduled, hasPage, t]
  );

  const scheduledPosts = useMemo(() => {
    if (!recentPosts?.posts) return [];
    return recentPosts.posts
      .filter((p: DashboardPost) => p.status === "scheduled" && p.scheduled_at)
      .sort((a: DashboardPost, b: DashboardPost) => new Date(a.scheduled_at || 0).getTime() - new Date(b.scheduled_at || 0).getTime())
      .slice(0, 5);
  }, [recentPosts]);

  const greeting = useMemo(() => {
    const hour = new Date().getHours();
    if (hour < 12) return t('dash_greeting_morning', 'Good morning');
    if (hour < 18) return t('dash_greeting_afternoon', 'Good afternoon');
    return t('dash_greeting_evening', 'Good evening');
  }, [t]);

  return (
    <div className="bg-n-page h-full flex flex-col overflow-hidden">

      <div className="max-w-6xl w-full mx-auto px-6 py-6 flex flex-col gap-6 flex-1 min-h-0">
        {/* Header */}
        <div className="flex items-center justify-between flex-shrink-0 animate-fade-in-up">
          <div>
            <h1 className="text-xl font-bold text-n-text tracking-tight flex items-center gap-2">
              {greeting}, {user?.name?.split(" ")[0] || t('dash_there', 'there')}
              <span className="inline-block">👋</span>
            </h1>
            <p className="text-sm text-n-text-lt mt-0.5 flex items-center gap-2">
              <Calendar size={13} className="text-n-blue" />
              {new Date().toLocaleDateString("en-US", { weekday: "long", month: "long", day: "numeric" })}
            </p>
          </div>
          <Link
            to="/compose"
            className="n-btn n-btn-primary gap-2 active:scale-95"
          >
            <Plus size={16} />
            {t('dash_new_post', 'New Post')}
          </Link>
        </div>

        {/* Reconnect banner — surfaces expired tokens immediately after login */}
        {needsReauthCount > 0 && (
          <Link
            to="/settings/accounts"
            className="flex items-center gap-3 rounded-n bg-n-red-bg border border-n-red/30 px-3 py-2.5 hover:bg-n-red-bg/80 transition-colors group"
            role="alert"
          >
            <div className="w-6 h-6 rounded-n-xs bg-n-red text-white flex items-center justify-center flex-shrink-0">
              <AlertTriangle size={14} strokeWidth={2.5} />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-[13px] font-semibold text-n-text">
                {t('dash_reauth_banner_title', {
                  count: needsReauthCount,
                  defaultValue:
                    needsReauthCount === 1
                      ? '1 social account needs to be reconnected'
                      : `${needsReauthCount} social accounts need to be reconnected`,
                })}
              </p>
              <p className="text-[12px] text-n-text-md mt-0.5">
                {t(
                  'dash_reauth_banner_body',
                  "Scheduled posts won't publish to these accounts until you reconnect.",
                )}
              </p>
            </div>
            <ArrowUpRight size={16} className="text-n-text-lt group-hover:text-n-red transition-colors flex-shrink-0" />
          </Link>
        )}

        {/* Onboarding */}
        {showOnboarding && isNewUser && (
          <div>
            <OnboardingChecklist steps={onboardingSteps} onDismiss={() => setShowOnboarding(false)} />
          </div>
        )}

        {/* Stats Row */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 flex-shrink-0">
          <StatCard
            label={t('dash_stat_published', 'Published')}
            value={overview?.posts?.published || 0}
            change={12}
            Icon={FileText}
            iconBg="bg-n-green-bg"
            iconColor="text-n-green"
            index={0}
          />
          <StatCard
            label={t('dash_stat_scheduled', 'Scheduled')}
            value={overview?.posts?.scheduled || 0}
            Icon={Calendar}
            iconBg="bg-n-blue-bg"
            iconColor="text-n-blue"
            index={1}
          />
          <StatCard
            label={t('dash_stat_engagement', 'Engagement')}
            value={
              (overview?.engagement?.likes || 0) +
              (overview?.engagement?.comments || 0) +
              (overview?.engagement?.shares || 0)
            }
            change={8}
            Icon={Heart}
            iconBg="bg-n-amber-bg"
            iconColor="text-n-amber"
            index={2}
          />
          <StatCard
            label={t('dash_stat_link_clicks', 'Link Clicks')}
            value={overview?.links?.clicks || 0}
            change={-3}
            Icon={MousePointerClick}
            iconBg="bg-n-red-bg"
            iconColor="text-n-red"
            index={3}
          />
        </div>

        {/* Main Grid: Posts + Sidebar — hidden for first-time users with no content */}
        {((publishedPosts?.posts?.length || 0) > 0 || scheduledPosts.length > 0 || (accounts?.accounts?.length || 0) > 0) && <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 flex-1 min-h-0">
          {/* Recent Posts — 2 cols */}
          <div className="lg:col-span-2 flex flex-col min-h-0 animate-fade-in-up" style={{ animationDelay: '0.15s' }}>
            <div className="n-card flex flex-col flex-1 min-h-0">
              <div className="n-card-header flex-shrink-0">
                <h2 className="text-sm font-semibold text-n-text tracking-tight">{t('dash_published_posts', 'Published Posts')}</h2>
                <Link to="/posts" className="text-xs font-semibold text-n-blue hover:underline transition-colors flex items-center gap-1">
                  {t('dash_view_all', 'View All')} <ArrowUpRight size={12} />
                </Link>
              </div>
              {(publishedPosts?.posts?.length || 0) > 0 ? (
                <div className="overflow-y-auto flex-1 no-scrollbar">
                  {(publishedPosts?.posts || []).slice(0, 6).map((post: DashboardPost) => (
                    <RecentPostRow key={post.id} post={post} />
                  ))}
                </div>
              ) : (
                <div className="n-empty">
                  <div className="n-empty-icon">
                    <FileText className="w-5 h-5" />
                  </div>
                  <p className="n-empty-title mb-1">{t('dash_no_posts_yet', 'No posts yet')}</p>
                  <p className="n-empty-desc mb-4">{t('dash_no_posts_message', 'Create your first post to get started')}</p>
                  <Link
                    to="/compose"
                    className="n-btn n-btn-primary gap-2 active:scale-95"
                  >
                    <PenSquare size={14} />
                    {t('dash_create_post_button', 'Create Post')}
                  </Link>
                </div>
              )}
            </div>
          </div>

          {/* Right Sidebar */}
          <div className="space-y-4 flex flex-col min-h-0 overflow-y-auto no-scrollbar animate-fade-in-up" style={{ animationDelay: '0.2s' }}>
            {/* Upcoming Schedule */}
            <div className="n-card flex-shrink-0">
              <div className="n-card-header">
                <h2 className="text-sm font-semibold text-n-text tracking-tight">{t('dash_coming_up', 'Coming Up')}</h2>
                <Link to="/calendar" className="text-xs font-semibold text-n-blue hover:underline transition-colors flex items-center gap-1">
                  {t('dash_calendar', 'Calendar')} <ArrowUpRight size={12} />
                </Link>
              </div>
              <div className="p-3">
                {scheduledPosts.length > 0 ? (
                  <div className="divide-y divide-n-border">
                    {scheduledPosts.map((post: DashboardPost) => (
                      <UpcomingPostItem key={post.id} post={post} />
                    ))}
                  </div>
                ) : (
                  <div className="n-empty py-6">
                    <div className="n-empty-icon w-8 h-8">
                      <Clock className="w-4 h-4" />
                    </div>
                    <p className="n-empty-title">{t('dash_nothing_scheduled', 'Nothing scheduled')}</p>
                    <p className="n-empty-desc mt-0.5">{t('dash_scheduled_appear_here', 'Posts you schedule will appear here')}</p>
                  </div>
                )}
              </div>
            </div>

            {/* Connected Accounts */}
            <div className="n-card flex-shrink-0">
              <div className="n-card-header">
                <h2 className="text-sm font-semibold text-n-text tracking-tight">{t('dash_accounts', 'Accounts')}</h2>
                <Link to="/accounts" className="text-xs font-semibold text-n-blue hover:underline transition-colors flex items-center gap-1">
                  {t('dash_manage', 'Manage')} <ArrowUpRight size={12} />
                </Link>
              </div>
              {accounts?.accounts?.length > 0 ? (
                <div className="p-3 space-y-1">
                  {accounts.accounts.slice(0, 5).map((acc: DashboardPost) => {
                    // Initials-tile fallback: we always render an initials
                    // square underneath; the <img> layers on top and is
                    // hidden via onError if the avatar URL fails (e.g.
                    // TikTok CDN returning 403 for expired tokens). This
                    // matches the pattern at line 182 above.
                    const displayName = acc.display_name || acc.username || '?';
                    const initials = getAccountInitials(displayName);
                    const tileStyle = getAccountTileStyle(displayName);
                    return (
                      <div key={acc.id} className="flex items-center gap-3 p-2.5 rounded-n-sm hover:bg-n-hover transition-all group">
                        <div className="relative flex-shrink-0">
                          <div
                            className="w-9 h-9 rounded-n-sm border border-n-border group-hover:border-n-blue/50 transition-colors flex items-center justify-center text-[11px] font-bold overflow-hidden relative"
                            style={tileStyle}
                            aria-label={displayName}
                          >
                            <span>{initials}</span>
                            {acc.avatar_url && (
                              <img
                                src={acc.avatar_url}
                                alt=""
                                aria-hidden="true"
                                className="absolute inset-0 w-full h-full object-cover"
                                onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
                              />
                            )}
                          </div>
                          <span className="absolute -bottom-0.5 -right-0.5 w-3 h-3 bg-n-green rounded-full border-2 border-white" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-semibold text-n-text truncate leading-tight">{displayName}</p>
                          <p className="text-xs text-n-text-lt capitalize mt-0.5">{acc.platform}</p>
                        </div>
                      </div>
                    );
                  })}
                  {accounts.accounts.length > 5 && (
                    <Link
                      to="/accounts"
                      className="block text-center text-xs font-semibold text-n-blue py-2.5 mt-1 hover:underline transition-colors"
                    >
                      {t('dash_more_accounts', '+{{count}} more accounts', { count: accounts.accounts.length - 5 })}
                    </Link>
                  )}
                </div>
              ) : (
                <div className="n-empty py-10">
                  <div className="n-empty-icon">
                    <Layers className="w-5 h-5" />
                  </div>
                  <p className="n-empty-title">{t('dash_no_accounts', 'No accounts connected')}</p>
                  <Link to="/accounts" className="text-xs font-semibold text-n-blue mt-2.5 inline-flex items-center gap-1 hover:underline transition-colors" >
                    {t('dash_connect_account', 'Connect account')} <ArrowUpRight size={11} />
                  </Link>
                </div>
              )}
            </div>
          </div>
        </div>}

        {/* Quick Actions */}
        <div className="flex-shrink-0 animate-fade-in-up" style={{ animationDelay: '0.3s' }}>
          <div className="flex items-center gap-3 mb-4">
            <h2 className="n-section-title whitespace-nowrap">{t('dash_quick_actions', 'Quick Actions')}</h2>
            <div className="n-divider flex-1" />
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
            {[
              { to: "/compose", Icon: Send, label: t('dash_action_create_post', 'Create Post'), bg: "bg-n-blue-bg", color: "text-n-blue" },
              { to: "/calendar", Icon: Calendar, label: t('dash_action_calendar', 'Calendar'), bg: "bg-n-green-bg", color: "text-n-green" },
              { to: "/ai", Icon: Sparkles, label: t('dash_action_ai_assistant', 'AI Assistant'), bg: "bg-n-amber-bg", color: "text-n-amber" },
              { to: "/builder", Icon: Layers, label: t('dash_action_page_builder', 'Page Builder'), bg: "bg-purple-50", color: "text-purple-500" },
              { to: "/links", Icon: Link2, label: t('dash_action_short_links', 'Short Links'), bg: "bg-pink-50", color: "text-pink-500" },
              { to: "/analytics", Icon: BarChart3, label: t('dash_action_analytics', 'Analytics'), bg: "bg-n-green-bg", color: "text-n-green" },
            ].map(({ to, Icon, label, bg, color }, i) => (
              <Link
                key={to}
                to={to}
                className="n-card group flex items-center gap-3 p-4 hover:-translate-y-0.5 hover:shadow-n transition-all duration-200 text-left animate-fade-in-up"
                style={{ animationDelay: `${0.35 + i * 0.05}s` }}
              >
                <div className={`w-10 h-10 ${bg} rounded-n flex items-center justify-center flex-shrink-0`}>
                  <Icon className={`w-5 h-5 ${color}`} />
                </div>
                <span className="text-sm font-semibold text-n-text group-hover:text-n-blue transition-colors leading-tight">{label}</span>
              </Link>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
